import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Upload, FileText, Video, Music, Image as ImageIcon, Presentation, Download, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { logIA } from "@/lib/iaLogger";

const KIND_ICONS = { pdf: FileText, video: Video, audio: Music, image: ImageIcon, presentation: Presentation, document: FileText, other: FileText };
const KIND_COLORS = { pdf: "text-red-600 bg-red-50", video: "text-purple-600 bg-purple-50", audio: "text-pink-600 bg-pink-50", image: "text-cyan-600 bg-cyan-50", presentation: "text-orange-600 bg-orange-50", document: "text-blue-600 bg-blue-50", other: "text-slate-600 bg-slate-50" };

export default function CoursLMSPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState({ subject: "", class_name: "" });
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", subject: "", class_name: "" });
  const [file, setFile] = useState(null);
  const isTeacher = user && ["teacher","admin","founder","super_admin","director"].includes(user.role);

  const load = async () => {
    try {
      const params = {};
      if (filter.subject) params.subject = filter.subject;
      if (filter.class_name) params.class_name = filter.class_name;
      const { data } = await api.get("/lms/courses", { params });
      setCourses(data);
    } catch { toast.error("Erreur chargement"); }
  };
  useEffect(() => { load(); }, [filter]);

  const upload = async () => {
    if (!file || !form.title || !form.subject || !form.class_name) return toast.error("Titre, matière, classe et fichier requis");
    setUploading(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("subject", form.subject);
    fd.append("class_name", form.class_name);
    fd.append("file", file);
    try {
      const { data } = await api.post("/lms/courses/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Cours téléversé (${(data.size / 1024 / 1024).toFixed(2)} Mo)`);
      setOpen(false);
      setForm({ title: "", description: "", subject: "", class_name: "" });
      setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); }
  };

  const download = (course) => {
    logIA("view_course", { resource_id: course.id, resource_title: course.title, subject: course.subject });
    const apiBase = api.defaults.baseURL;
    const token = localStorage.getItem("mazid_token");
    fetch(`${apiBase}/lms/courses/${course.id}/download`, { headers: { Authorization: `Bearer ${token}` }})
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = course.filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const del = async (id) => {
    if (!confirm("Supprimer ce cours ?")) return;
    try {
      await api.delete(`/lms/courses/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="lms-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>ESPACE COURS — LMS</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Bibliothèque de cours</h1>
          <p className="text-slate-500 mt-1">{courses.length} support(s) disponible(s){user?.role === "student" && ` pour votre classe`}.</p>
        </div>
        {isTeacher && (
          <Button onClick={() => setOpen(true)} className="bg-[#1E3A8A] text-white" data-testid="btn-upload"><Plus className="h-4 w-4 mr-2" />Déposer un cours</Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input placeholder="Filtrer par matière..." value={filter.subject} onChange={e => setFilter({...filter, subject: e.target.value})} data-testid="filter-subject" />
        {isTeacher && <Input placeholder="Filtrer par classe..." value={filter.class_name} onChange={e => setFilter({...filter, class_name: e.target.value})} data-testid="filter-class" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => {
          const Icon = KIND_ICONS[c.kind] || FileText;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition-shadow" data-testid={`course-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${KIND_COLORS[c.kind]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-xs uppercase font-bold text-slate-400">{c.kind}</span>
              </div>
              <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">{c.title}</div>
              <div className="text-xs text-slate-500 mb-2"><strong>{c.subject}</strong> — {c.class_name}</div>
              {c.description && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{c.description}</p>}
              <div className="text-xs text-slate-400 mb-3">
                {(c.size_bytes / 1024 / 1024).toFixed(2)} Mo · {new Date(c.created_at).toLocaleDateString("fr-FR")}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => download(c)} className="bg-[#166534] text-white flex-1" data-testid={`dl-${c.id}`}><Download className="h-3 w-3 mr-1" />Télécharger</Button>
                {isTeacher && <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>}
              </div>
            </div>
          );
        })}
        {courses.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
            {isTeacher ? "Aucun cours déposé. Cliquez sur \"Déposer un cours\" pour commencer." : "Aucun cours disponible pour votre classe pour le moment."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Déposer un support de cours</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Chapitre 3 — Les fractions" data-testid="up-title" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="up-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Matière *</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Mathématiques" data-testid="up-subject" /></div>
              <div><Label>Classe *</Label><Input value={form.class_name} onChange={e => setForm({...form, class_name: e.target.value})} placeholder="Primaire CE2" data-testid="up-class" /></div>
            </div>
            <div>
              <Label>Fichier * (PDF, Word, PPT, image, vidéo, audio — max 100 Mo)</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi,.mp3,.wav,.ogg" onChange={e => setFile(e.target.files?.[0])} data-testid="up-file" />
              {file && <div className="text-xs text-slate-500 mt-1">{file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={upload} disabled={uploading} className="bg-[#1E3A8A] text-white" data-testid="up-submit">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Téléverser</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
